/**
 * Haar-feature object detection.
 *
 * This code is a rewrite from:
 * and https://github.com/inspirit/jsfeat implementation
 * @author Eugene Zatepyakin / http://inspirit.ru/
 *
 * @copyright Realeyes OU. All rights reserved.
 */


/**
 * Evaluates a cascade classifier on a selected window.
 *
 * @param {Uint32Array} intSum Integral image
 * @param {*} stages Classifier stages
 * @param {number} scale Window scale
 * @param {number} x Window x
 * @param {number} y Window y
 * @param {number} w1 Width + 1 // FIXME: find a way to remove this arg
 * @param {number} invArea Inversed window area
 * @param {number} std Standard deviation
 * @return {number}
 */
function evalClassifier(intSum, stages, scale, x, y, w1, invArea, std) {
    let stageSum = 0;

    for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        const stageThresh = stage.threshold;
        const trees = stage.simpleClassifiers;
        stageSum = 0;

        for (let j = 0; j < trees.length; j++) {
            const tree = trees[j];
            const { features } = tree;
            let treeSum = 0;

            for (let k = 0; k < features.length; k++) {
                const feature = features[k];
                const fiA = ~~(x + (feature[0] * scale)) + (~~(y + (feature[1] * scale)) * w1);
                const fw = ~~(feature[2] * scale);
                const fh = ~~(feature[3] * scale);
                const fiC = fh * w1;

                treeSum += ((intSum[fiA]
                           - intSum[fiA + fw]
                           - intSum[fiA + fiC])
                           + intSum[fiA + fiC + fw]) * feature[4];
            }
            stageSum += treeSum * invArea < tree.threshold * std ? tree.leftVal : tree.rightVal;
        }
        if (stageSum < stageThresh) return 0;
    }

    return stageSum;
}


/**
 * Evaluates classifier using sliding window approach
 *
 * @param {Uint32Array} intSum Integral image
 * @param {Uint32Array} intSqSum Squared integral image
 * @param {number} width Image width
 * @param {number} height Image height
 * @param {number} scale Image scale
 * @param {*} classifier Haar cascade classifier
 * @return {Array}
 */
function detectSingleScale(intSum, intSqSum, width, height, scale, classifier) {
    const stages = classifier.complexClassifiers;
    const winWidth = classifier.size[0] * scale | 0;
    const winHeight = classifier.size[1] * scale | 0;
    const stepX = (0.5 * scale) + 1.5 | 0;
    const stepY = stepX;
    const w1 = width + 1 | 0;
    const invArea = 1.0 / (winWidth * winHeight);
    const rects = [];

    for (let y = 0; y + winHeight < height; y += stepY) {
        let satIndex = y * w1;
        for (let x = 0; x + winWidth < width; x += stepX, satIndex += stepX) {
            const satIndex1 = satIndex + winWidth;
            const satIndex2 = (winHeight * w1) + satIndex;
            const satIndex3 = (winHeight * w1) + winWidth;

            const mean = ((intSum[satIndex]
                         - intSum[satIndex1]
                         - intSum[satIndex2])
                         + intSum[satIndex + satIndex3]) * invArea;
            const variance = (((intSqSum[satIndex]
                              - intSqSum[satIndex1]
                              - intSqSum[satIndex2])
                              + intSqSum[satIndex + satIndex3]) * invArea) - (mean * mean);
            const std = variance > 0 ? Math.sqrt(variance) : 1;

            const stageSum = evalClassifier(intSum, stages, scale, x, y, w1, invArea, std);

            if (stageSum > 0) {
                rects.push({
                    x,
                    y,
                    width: winWidth,
                    height: winHeight,
                    neighbor: 1,
                    confidence: stageSum,
                });
                x += stepX;
                satIndex += stepX;
            }
        }
    }

    return rects;
}


/**
 * Partitions rects into similarity classes.
 *
 * @param {Array} rects Detected rects
 * @return {Array}
 */
function createSimilarityTree(rects) {
    const tree = [];

    for (let i = 0; i < rects.length; i++) {
        tree[i] = {
            parent: -1,
            element: rects[i],
            rank: 0,
        };
    }

    for (let i = 0; i < rects.length; i++) {
        let root = i;
        while (tree[root].parent !== -1) root = tree[root].parent;

        for (let j = 0; j < rects.length; ++j) {
            const rect1 = tree[i].element;
            const rect2 = tree[j].element;
            const distance = ((rect1.width * 0.25) + 0.5) | 0;

            if (
                i !== j && tree[j].element
                && rect2.x <= rect1.x + distance
                && rect2.x >= rect1.x - distance
                && rect2.y <= rect1.y + distance
                && rect2.y >= rect1.y - distance
                && rect2.width <= (((rect1.width * 1.5) + 0.5) | 0)
                && (((rect2.width * 1.5) + 0.5) | 0) >= rect1.width
            ) {
                let root2 = j;

                while (tree[root2].parent !== -1) root2 = tree[root2].parent;

                if (root2 !== root) {
                    if (tree[root].rank > tree[root2].rank) {
                        tree[root2].parent = root;
                    } else {
                        tree[root].parent = root2;
                        // TODO: should be refactored, not sure how the enter else branch here
                        // istanbul ignore else
                        if (tree[root].rank === tree[root2].rank) tree[root2].rank++;
                        root = root2;
                    }

                    /* compress path from node2 to the root: */
                    let temp;
                    let node2 = j;
                    while (tree[node2].parent !== -1) {
                        temp = node2;
                        node2 = tree[node2].parent;
                        tree[temp].parent = root;
                    }

                    /* compress path from node to the root: */
                    node2 = i;
                    while (tree[node2].parent !== -1) {
                        temp = node2;
                        node2 = tree[node2].parent;
                        tree[temp].parent = root;
                    }
                }
            }
        }
    }

    return tree;
}


/**
 * Computes average rectangle for each cluster.
 *
 * @param {Array} rects Detected rectangles
 * @param {Array} labels Class labels
 * @param {number} numClasses Number of classes
 * @param {number} minNeighbors Minimum neighbors for returned groups
 * @return {Array}
 */
function createAverageRectangles(rects, labels, numClasses, minNeighbors) {
    const groups = [];
    for (let i = 0; i < numClasses; i++) {
        groups[i] = {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            neighbors: 0,
            confidence: 0,
        };
    }

    // count number of neighbors
    for (let i = 0; i < rects.length; i++) {
        const rect = rects[i];
        const group = groups[labels[i]];

        if (group.neighbors === 0) {
            group.confidence = rect.confidence;
        }

        group.neighbors += 1;
        group.x += rect.x;
        group.y += rect.y;
        group.width += rect.width;
        group.height += rect.height;
        group.confidence = Math.max(group.confidence, rect.confidence);
    }

    // calculate average bounding box
    for (let i = 0; i < groups.length; i++) {
        const n = groups[i].neighbors;
        // TODO: create a sample image to test else branch
        // istanbul ignore else
        if (n >= minNeighbors) {
            const group = groups[i];

            group.x /= n;
            group.y /= n;
            group.width /= n;
            group.height /= n;
        } else {
            groups.splice(i, 1);
        }
    }

    return groups;
}


/**
 * Filters out small rectangles inside larger rectangles.
 * TODO: create a sample image to test nested rectangles
 *
 * @param {Array} rects Detected rectangles
 * @return {Array}
 */
function filterNestedRectangles(rects) {
    const result = [];
    const rLen = rects.length;

    for (let i = 0; i < rLen; i++) {
        const rect1 = rects[i];
        let j = i + 1;

        for (; j < rLen; j++) {
            const rect2 = rects[j];
            const distance = ((rect2.width * 0.25) + 0.5) | 0;

            // istanbul ignore next
            if (
                (rect1.x >= rect2.x - distance
                && rect1.y >= rect2.y - distance
                && rect1.x + rect1.width <= rect2.x + rect2.width + distance
                && rect1.y + rect1.height <= rect2.y + rect2.height + distance
                && (rect2.neighbors > Math.max(3, rect1.neighbors) || rect1.neighbors < 3))
                || (rect2.x >= rect1.x - distance
                && rect2.y >= rect1.y - distance
                && rect2.x + rect2.width <= rect1.x + rect1.width + distance
                && rect2.y + rect2.height <= rect1.y + rect1.height + distance
                && (rect1.neighbors > Math.max(3, rect2.neighbors) || rect2.neighbors < 3))

            ) break;
        }

        // istanbul ignore else
        if (j === rLen) result.push(rect1);
    }

    return result;
}


export default {
    /**
     * Evaluates a cascade classifier.
     *
     * @param {Uint32Array} intSum Integral image
     * @param {Uint32Array} intSqSum Squared integral image
     * @param {number} width Image width
     * @param {number} height Image height
     * @param {*} classifier Haar cascade classifier
     * @param {number} scaleFactor Scale factor for each step
     * @param {number} startScale Initial scale
     * @return {Array}
     */
    detect(intSum, intSqSum, width, height, classifier, scaleFactor, startScale) {
        const winWidth = classifier.size[0];
        const winHeight = classifier.size[1];
        let scale = startScale;
        let rects = [];

        while (scale * winWidth < width && scale * winHeight < height) {
            rects = rects.concat(detectSingleScale(intSum, intSqSum, width, height, scale, classifier));
            scale *= scaleFactor;
        }

        return rects;
    },

    /**
     * Groups rectangles together. For each group of related rectangles,
     * a representative mean rectangle is returned.
     * TODO: refactor labeling
     *
     * @param {Array} rects Detected rectangles
     * @param {number} minNeighbors Minimum neighbors for returned groups
     * @return {Array}
     */
    groupRectangles(rects, minNeighbors = 1) {
        const node = createSimilarityTree(rects);
        const labels = [];
        let numClasses = 0;

        for (let i = 0; i < rects.length; i++) {
            let j = -1;
            let node1 = i;
            while (node[node1].parent !== -1) node1 = node[node1].parent;
            if (node[node1].rank >= 0) node[node1].rank = ~numClasses++;
            j = ~node[node1].rank;
            labels[i] = j;
        }

        const groups = createAverageRectangles(rects, labels, numClasses, minNeighbors);

        return filterNestedRectangles(groups);
    },
};
